import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import EmailHtml from 'assets/components/html-templates/email/EmailTemplate';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import RichTextInput from 'assets/components/inputs/richTextInput/RichTextInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Modal from 'assets/components/page/templates/Modal';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import { getLocales } from 'assets/locales/Locale';
import { isEmail, isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { first } from 'lodash';
import ContractRefundPaymentEmailTemplate from 'models/core/contractRefundPaymentEmailTemplate/Model.ContractRefundPaymentEmailTemplate';
import ContractRefundPaymentEmailTemplateTags from 'module/management/components/contractRefundPaymentEmailTemplate/ContractRefundPaymentEmailTemplateTags';
import { useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import { useContractRefundPaymentProvider } from '../Finance.ContractRefundPayment.Hooks';

export default function ContractRefundPaymentSendEmail() {
  const { lang } = getLocales();
  const { sendEmail, emailTemplates, pageRouter } = useContractRefundPaymentProvider();
  const contractRefundsRemittanceAdviceEmailTemplate = first(emailTemplates);

  const form = useForm<Api.Finance.ContractRefundPaymentEmail.SendEmail.IRequest>({
    default: {
      htmlBody: renderToString(
        EmailHtml({
          body: 'Enter custom message here.',
        })
      ),
      additionalRecipientEmails: [],
    },
    validation: (data, errors) => {
      if (!data.senderEmail) errors.senderEmail = lang.mustNotBeEmpty;
      if (data.senderEmail && !isEmailDomain(data.senderEmail, 'goautoshield.com'))
        errors.senderEmail = lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com');
      if (!data.senderName) errors.senderName = lang.mustNotBeEmpty;
      if (!data.subject) errors.subject = lang.mustNotBeEmpty;
      if (!data.htmlBody) errors.htmlBody = lang.mustNotBeEmpty;
      if (
        !data.sendToRemittanceEmail &&
        !data.sendToOwner &&
        (!data.additionalRecipientEmails || data?.additionalRecipientEmails?.every((it) => !isEmail(it))) &&
        !data.recipientsWithRole?.length
      )
        errors.additionalRecipientEmails = lang.mustNotBeEmpty;
    },
    onSubmit: (data) => sendEmail(new ContractRefundPaymentEmailTemplate(data)),
  });

  useEffect(() => {
    if (contractRefundsRemittanceAdviceEmailTemplate) {
      form.update({ ...contractRefundsRemittanceAdviceEmailTemplate });
    }
  }, [contractRefundsRemittanceAdviceEmailTemplate]);

  return (
    <Modal
      subcomponents={ThemeModalDefault}
      header={lang.emailTemplate}
      onClose={() => pageRouter.updateParams({})}
      render={
        <FormWrapper>
          <InputWrapper>
            <TextInput
              name="senderName"
              label={lang.senderName}
              value={form.data.senderName}
              error={form.errors.senderName}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
            <TextInput
              name="senderEmail"
              label={lang.senderEmail}
              value={form.data.senderEmail}
              error={form.errors.senderEmail}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
          </InputWrapper>
          <h2>{lang.sendTo}</h2>
          <CheckWrapper>
            <CheckInput
              name="sendToRemittanceEmail"
              label={lang.remittanceEmailAddress}
              class={inputStyles.check.switch}
              value={form.data.sendToRemittanceEmail}
              onChange={form.update}
              htmlElementProps={{ title: form.data.sendToRemittanceEmail ? lang.disable : lang.enable }}
            />
            <CheckInput
              name="sendToOwner"
              label={lang.owner}
              class={inputStyles.check.switch}
              value={form.data.sendToOwner}
              onChange={form.update}
              htmlElementProps={{ title: form.data.sendToOwner ? lang.disable : lang.enable }}
            />
          </CheckWrapper>
          <h2>{lang.additionalRecipientEmails}</h2>
          <DataMap
            data={[...(form.data.additionalRecipientEmails || []), '']}
            render={({ data: email, index }) => (
              <TextInput
                key={index}
                name="email"
                label={lang.email}
                value={email}
                error={form.errors.additionalRecipientEmails}
                onChange={({ email }) => {
                  let additionalRecipientEmails = [...(form?.data?.additionalRecipientEmails || [])];
                  additionalRecipientEmails.splice(index, 1, email);
                  additionalRecipientEmails = additionalRecipientEmails?.filter((it) => it);
                  form.update({ additionalRecipientEmails });
                }}
                icon={<Icon class="if-quill-pen" />}
              />
            )}
          />
          <h2>{lang.email}</h2>
          <TextInput
            name="subject"
            label={lang.subject}
            value={form.data.subject}
            error={form.errors.subject}
            onChange={form.update}
            icon={<Icon class="if-quill-pen" />}
          />
          <RichTextInput
            name="htmlBody"
            label={lang.htmlBody}
            value={form.data.htmlBody}
            error={form.errors.htmlBody}
            onChange={form.update}
            icon={<Icon class="if-quill-pen" />}
            customActions={<ContractRefundPaymentEmailTemplateTags />}
          />
        </FormWrapper>
      }
      footer={
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={pageRouter.updateParams}
            data={{}}
            media="fas-ban"
            render={lang.cancel}
            class={ThemeButtonCircle}
          />
          <Button onClick={form.submit} render={lang.sendEmail} class={ThemeButtonCircle} active />
        </div>
      }
    />
  );
}
